import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Stepper,
  Step,
  StepLabel,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  RadioGroup,
  Radio,
  FormControlLabel,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Checkbox,
  IconButton,
  Chip,
  CircularProgress,
  Stack,
  Tooltip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import RefreshIcon from "@mui/icons-material/Refresh";

const steps = ["Select Document Type", "Configure Fields", "Review & Save"];

const DATA_TYPES = [
  { label: "Text", value: "STRING" },
  { label: "Date", value: "DATE" },
  { label: "Number", value: "DECIMAL" },
  { label: "Boolean", value: "BOOLEAN" },
];

const DEFAULT_BLUE_COLLAR_PRESETS = {
  DRIVING_LICENSE: [
    { field_key: "licence_number", display_name: "Licence Number", data_type: "STRING", is_required: true },
    { field_key: "name", display_name: "Employee Name", data_type: "STRING", is_required: true },
    { field_key: "expiry_date", display_name: "Expiry Date", data_type: "DATE", is_required: true },
  ],
  PAN: [
    { field_key: "pan_number", display_name: "PAN Number", data_type: "STRING", is_required: true },
    { field_key: "name", display_name: "Name on PAN", data_type: "STRING", is_required: true },
    { field_key: "dob", display_name: "Date of Birth", data_type: "DATE", is_required: true },
  ],
  AADHAAR: [
    { field_key: "aadhaar_number", display_name: "Aadhaar Number", data_type: "STRING", is_required: true },
    { field_key: "name", display_name: "Full Name", data_type: "STRING", is_required: true },
    { field_key: "dob", display_name: "Date of Birth", data_type: "DATE", is_required: true },
    { field_key: "gender", display_name: "Gender", data_type: "STRING", is_required: false },
  ],
};

const AddDocumentModal = ({
  open,
  onClose,
  masterDocs = [],
  configuredDocs = [],
  onSaveConfiguration,
}) => {
  const [activeStep, setActiveStep] = useState(0);

  // Step 1: Selection
  const [hiringType, setHiringType] = useState("BLUE_COLLAR");
  const [docMode, setDocMode] = useState("CATALOG"); // "CATALOG" or "CUSTOM"
  const [selectedDocId, setSelectedDocId] = useState("");
  const [customDisplayName, setCustomDisplayName] = useState("");
  const [customDocType, setCustomDocType] = useState("");
  const [availableDocs, setAvailableDocs] = useState([]);

  // Step 2: Verification & Fields
  const [verificationType, setVerificationType] = useState("MANUAL"); // MANUAL or AI
  const [fields, setFields] = useState([]);
  const [fieldErrors, setFieldErrors] = useState({});
  const [aiPromptInstructions, setAiPromptInstructions] = useState("");

  const getDefaultPromptForDoc = (name) => {
    return `Extract all configured fields from ${name} in structured JSON format. Verify document authenticity, check that critical identifiers and candidate name are accurate, and validate expiry dates if applicable.`;
  };

  // Loading state
  const [saving, setSaving] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  // Update available master documents when hiring type changes
  useEffect(() => {
    if (!open) return;
    const typeGroup = masterDocs.find(
      (m) => m.hiring_type?.toUpperCase() === hiringType.toUpperCase()
    );
    const docs = typeGroup?.documents || [];
    setAvailableDocs(docs);

    if (docs.length > 0 && !selectedDocId) {
      setSelectedDocId(docs[0].document_requirement_id);
    }
  }, [hiringType, masterDocs, open]);

  // Load preset suggestions when document selection changes
  useEffect(() => {
    if (docMode === "CUSTOM") {
      setFields([
        {
          id: `field_${Date.now()}_1`,
          field_key: "document_number",
          display_name: `${customDisplayName || "Document"} Number`,
          data_type: "STRING",
          is_required: true,
        },
        {
          id: `field_${Date.now()}_2`,
          field_key: "holder_name",
          display_name: "Holder Full Name",
          data_type: "STRING",
          is_required: true,
        },
      ]);
      return;
    }

    const selectedDoc = availableDocs.find((d) => d.document_requirement_id === selectedDocId);
    const docKey = selectedDoc?.document_type?.toUpperCase();
    if (docKey && DEFAULT_BLUE_COLLAR_PRESETS[docKey]) {
      setFields(DEFAULT_BLUE_COLLAR_PRESETS[docKey].map((f) => ({ ...f, id: `field_${Date.now()}_${Math.random()}` })));
    } else {
      setFields([
        {
          id: `field_${Date.now()}_1`,
          field_key: "document_number",
          display_name: "Document Number",
          data_type: "STRING",
          is_required: true,
        },
      ]);
    }
  }, [selectedDocId, availableDocs, docMode, customDisplayName]);

  const selectedDocObj = availableDocs.find((d) => d.document_requirement_id === selectedDocId);

  // Initialize and synchronize default AI prompt when document changes
  useEffect(() => {
    const docName =
      docMode === "CUSTOM"
        ? customDisplayName || "the document"
        : selectedDocObj?.display_name || "the document";
    setAiPromptInstructions(getDefaultPromptForDoc(docName));
  }, [selectedDocId, availableDocs, docMode, customDisplayName]);

  const handleClose = () => {
    setActiveStep(0);
    setErrorMsg("");
    setFieldErrors({});
    setDocMode("CATALOG");
    setCustomDisplayName("");
    setCustomDocType("");
    onClose();
  };

  const handleNext = () => {
    if (activeStep === 0) {
      if (docMode === "CATALOG" && !selectedDocId) {
        setErrorMsg("Please select a document type from the catalog.");
        return;
      }
      if (docMode === "CUSTOM") {
        if (!customDisplayName.trim()) {
          setErrorMsg("Please enter a display name for your custom document (e.g., Passport).");
          return;
        }
        if (!customDocType.trim()) {
          setErrorMsg("Please enter a document code for your custom document (e.g., PASSPORT).");
          return;
        }
      }
      setErrorMsg("");
      setActiveStep(1);
    } else if (activeStep === 1) {
      if (fields.length === 0) {
        setErrorMsg("Please add at least one field to extract.");
        return;
      }

      const errors = {};
      const keyPattern = /^[a-z][a-z0-9_]*$/;
      const keysSeen = new Set();

      fields.forEach((f, idx) => {
        if (!f.field_key || !keyPattern.test(f.field_key.trim())) {
          errors[`key_${idx}`] = "Lowercase snake_case only (e.g. licence_number)";
        } else if (keysSeen.has(f.field_key.trim())) {
          errors[`key_${idx}`] = "Duplicate field key";
        }
        keysSeen.add(f.field_key.trim());

        if (!f.display_name || !f.display_name.trim()) {
          errors[`name_${idx}`] = "Display name is required";
        }
      });

      if (Object.keys(errors).length > 0) {
        setFieldErrors(errors);
        setErrorMsg("Please fix validation errors in the fields table.");
        return;
      }

      if (verificationType === "AI" && !aiPromptInstructions.trim()) {
        setErrorMsg("Please provide AI prompt instructions or click 'Reset Prompt'.");
        return;
      }

      setErrorMsg("");
      setFieldErrors({});
      setActiveStep(2);
    }
  };

  const handleBack = () => {
    setErrorMsg("");
    setActiveStep((prev) => Math.max(0, prev - 1));
  };

  const handleAddField = () => {
    setFields((prev) => [
      ...prev,
      {
        id: `field_${Date.now()}_${Math.random()}`,
        field_key: "",
        display_name: "",
        data_type: "STRING",
        is_required: true,
      },
    ]);
  };

  const handleRemoveField = (index) => {
    setFields((prev) => prev.filter((_, idx) => idx !== index));
    // Clear errors for that field
    setFieldErrors((prev) => {
      const copy = { ...prev };
      delete copy[`key_${index}`];
      delete copy[`name_${index}`];
      return copy;
    });
  };

  const handleFieldChange = (index, fieldName, value) => {
    setFields((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [fieldName]: value };

      // Auto-generate snake_case field_key from display_name if field_key is empty
      if (fieldName === "display_name" && !updated[index].field_key) {
        const autoKey = value
          .toLowerCase()
          .trim()
          .replace(/[^a-z0-9]+/g, "_")
          .replace(/^_+|_+$/g, "");
        if (/^[a-z]/.test(autoKey)) {
          updated[index].field_key = autoKey;
        }
      }

      return updated;
    });

    if (fieldErrors[`key_${index}`] || fieldErrors[`name_${index}`]) {
      setFieldErrors((prev) => {
        const copy = { ...prev };
        delete copy[`key_${index}`];
        delete copy[`name_${index}`];
        return copy;
      });
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setErrorMsg("");
    try {
      const isCustom = docMode === "CUSTOM";
      const docType = isCustom
        ? customDocType.trim().toUpperCase().replace(/\s+/g, "_")
        : (selectedDocObj?.document_type || "DOCUMENT");
      const dispName = isCustom
        ? customDisplayName.trim()
        : (selectedDocObj?.display_name || "Document");

      await onSaveConfiguration({
        hiringType,
        isCustom,
        documentRequirementId: isCustom ? null : selectedDocId,
        documentType: docType,
        displayName: dispName,
        verificationMode: verificationType,
        fields: fields,
        aiPrompt: verificationType === "AI" ? aiPromptInstructions : null,
      });
      handleClose();
    } catch (err) {
      console.error("Save configuration failed:", err);
      setErrorMsg(err.response?.data?.detail || err.message || "Failed to save configuration.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={(event, reason) => {
        if (reason !== "backdropClick" && !saving) handleClose();
      }}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          p: 1,
        },
      }}
    >
      {/* Header */}
      <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", pb: 1 }}>
        <Typography variant="h6" fontWeight={700} color="#111827">
          Add Document Configuration
        </Typography>
        <IconButton onClick={handleClose} disabled={saving} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      {/* Stepper */}
      <Box sx={{ px: 3, pt: 1, pb: 2 }}>
        <Stepper activeStep={activeStep} alternativeLabel>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
      </Box>

      <DialogContent sx={{ px: 3, py: 2 }}>
        {errorMsg && (
          <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }} onClose={() => setErrorMsg("")}>
            {errorMsg}
          </Alert>
        )}

        {/* STEP 1: Select Document Type */}
        {activeStep === 0 && (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5, mt: 1 }}>
            <FormControl fullWidth size="medium">
              <InputLabel id="hiring-type-select-label">Hiring Type *</InputLabel>
              <Select
                labelId="hiring-type-select-label"
                value={hiringType}
                label="Hiring Type *"
                onChange={(e) => setHiringType(e.target.value)}
              >
                <MenuItem value="BLUE_COLLAR">Blue Collar</MenuItem>
                <MenuItem value="WHITE_COLLAR">White Collar</MenuItem>
              </Select>
            </FormControl>

            <Box sx={{ p: 2, bgcolor: "#F9FAFB", borderRadius: 2, border: "1px solid #E5E7EB" }}>
              <Typography variant="subtitle2" fontWeight={600} color="#374151" gutterBottom>
                Document Source
              </Typography>
              <RadioGroup
                row
                value={docMode}
                onChange={(e) => {
                  setDocMode(e.target.value);
                  setErrorMsg("");
                }}
              >
                <FormControlLabel
                  value="CATALOG"
                  control={<Radio size="small" />}
                  label="Select from Standard Catalog"
                />
                <FormControlLabel
                  value="CUSTOM"
                  control={<Radio size="small" />}
                  label="+ Create Custom Document Type"
                />
              </RadioGroup>
            </Box>

            {docMode === "CATALOG" ? (
              <FormControl fullWidth size="medium">
                <InputLabel id="doc-type-select-label">Document Type *</InputLabel>
                <Select
                  labelId="doc-type-select-label"
                  value={selectedDocId}
                  label="Document Type *"
                  onChange={(e) => setSelectedDocId(e.target.value)}
                >
                  {availableDocs.map((doc) => (
                    <MenuItem key={doc.document_requirement_id} value={doc.document_requirement_id}>
                      {doc.display_name} ({doc.document_type})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            ) : (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                <TextField
                  fullWidth
                  label="Document Display Name *"
                  placeholder="e.g. Passport, Salary Slip, Voter ID"
                  value={customDisplayName}
                  onChange={(e) => {
                    const val = e.target.value;
                    setCustomDisplayName(val);
                    const autoType = val.toUpperCase().trim().replace(/[^A-Z0-9]+/g, "_").replace(/^_+|_+$/g, "");
                    setCustomDocType(autoType);
                  }}
                  helperText="User-friendly name shown to the candidate and recruiters"
                />
                <TextField
                  fullWidth
                  label="Document Type Code *"
                  placeholder="e.g. PASSPORT, SALARY_SLIP"
                  value={customDocType}
                  onChange={(e) => setCustomDocType(e.target.value.toUpperCase().replace(/\s+/g, "_"))}
                  helperText="Unique uppercase system identifier"
                />
              </Box>
            )}

            {(docMode === "CUSTOM" ? customDisplayName : selectedDocObj) && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                  p: 2,
                  bgcolor: "#EFF6FF",
                  borderRadius: 2,
                  border: "1px solid #BFDBFE",
                  color: "#1E40AF",
                }}
              >
                <InfoOutlinedIcon fontSize="small" />
                <Typography variant="body2">
                  You are configuring extraction and verification settings for{" "}
                  <strong>
                    {docMode === "CUSTOM" ? (customDisplayName || "Custom Document") : selectedDocObj?.display_name}
                  </strong>{" "}
                  ({hiringType === "BLUE_COLLAR" ? "Blue Collar" : "White Collar"}).
                </Typography>
              </Box>
            )}
          </Box>
        )}

        {/* STEP 2: Configure Fields */}
        {activeStep === 1 && (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
            {/* Fields Configuration Table */}
            <Box sx={{ mt: 0 }}>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1.5 }}>
                <Box>
                  <Typography variant="subtitle1" fontWeight={700} color="#111827">
                    Document Data Fields to Extract
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Define the fields to extract from{" "}
                    {docMode === "CUSTOM"
                      ? customDisplayName || "the document"
                      : selectedDocObj?.display_name || "the document"}{" "}
                    so they pre-fill for verification.
                  </Typography>
                </Box>
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={handleAddField}
                  sx={{
                    textTransform: "none",
                    borderRadius: 2,
                    bgcolor: "#2563EB",
                    "&:hover": { bgcolor: "#1D4ED8" },
                  }}
                >
                  Add Field
                </Button>
              </Box>

              <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
                <Table size="small">
                  <TableHead sx={{ bgcolor: "#F9FAFB" }}>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600, width: "30%" }}>Field Key *</TableCell>
                      <TableCell sx={{ fontWeight: 600, width: "30%" }}>Field Name *</TableCell>
                      <TableCell sx={{ fontWeight: 600, width: "22%" }}>Data Type *</TableCell>
                      <TableCell sx={{ fontWeight: 600, width: "10%" }} align="center">
                        Required
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600, width: "8%" }} align="center">
                        Actions
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {fields.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={5} align="center" sx={{ py: 3, color: "text.secondary" }}>
                          No fields configured yet. Click "+ Add Field" above to add one.
                        </TableCell>
                      </TableRow>
                    ) : (
                      fields.map((field, idx) => (
                        <TableRow key={field.id || idx}>
                          <TableCell>
                            <TextField
                              size="small"
                              fullWidth
                              placeholder="e.g. licence_number"
                              value={field.field_key}
                              onChange={(e) => handleFieldChange(idx, "field_key", e.target.value)}
                              error={Boolean(fieldErrors[`key_${idx}`])}
                              helperText={fieldErrors[`key_${idx}`]}
                              inputProps={{ style: { fontSize: "0.875rem" } }}
                            />
                          </TableCell>
                          <TableCell>
                            <TextField
                              size="small"
                              fullWidth
                              placeholder="e.g. Licence Number"
                              value={field.display_name}
                              onChange={(e) => handleFieldChange(idx, "display_name", e.target.value)}
                              error={Boolean(fieldErrors[`name_${idx}`])}
                              helperText={fieldErrors[`name_${idx}`]}
                              inputProps={{ style: { fontSize: "0.875rem" } }}
                            />
                          </TableCell>
                          <TableCell>
                            <Select
                              size="small"
                              fullWidth
                              value={field.data_type}
                              onChange={(e) => handleFieldChange(idx, "data_type", e.target.value)}
                              sx={{ fontSize: "0.875rem" }}
                            >
                              {DATA_TYPES.map((dt) => (
                                <MenuItem key={dt.value} value={dt.value}>
                                  {dt.label}
                                </MenuItem>
                              ))}
                            </Select>
                          </TableCell>
                          <TableCell align="center">
                            <Checkbox
                              size="small"
                              checked={field.is_required}
                              onChange={(e) => handleFieldChange(idx, "is_required", e.target.checked)}
                            />
                          </TableCell>
                          <TableCell align="center">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleRemoveField(idx)}
                              disabled={fields.length === 1}
                            >
                              <DeleteOutlineIcon fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>

              {/* AI Verification Prompt & System Rules Card */}
              {verificationType === "AI" && (
                <Box
                  sx={{
                    mt: 2.5,
                    p: 2.5,
                    bgcolor: "#FAF5FF",
                    borderRadius: 2.5,
                    border: "1.5px solid #E9D5FF",
                  }}
                >
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <AutoAwesomeIcon sx={{ color: "#7E22CE", fontSize: 20 }} />
                      <Typography variant="subtitle2" fontWeight={700} color="#581C87">
                        AI Verification Prompt & System Rules
                      </Typography>
                      <Chip
                        label="LLM Extraction"
                        size="small"
                        sx={{ bgcolor: "#F3E8FF", color: "#7E22CE", fontWeight: 700, fontSize: "0.68rem", height: 20 }}
                      />
                    </Box>
                    <Button
                      size="small"
                      startIcon={<RefreshIcon sx={{ fontSize: 16 }} />}
                      onClick={() => {
                        const docName =
                          docMode === "CUSTOM"
                            ? customDisplayName || "the document"
                            : selectedDocObj?.display_name || "the document";
                        setAiPromptInstructions(getDefaultPromptForDoc(docName));
                      }}
                      sx={{ textTransform: "none", color: "#7E22CE", fontSize: "0.75rem" }}
                    >
                      Reset Prompt
                    </Button>
                  </Box>
                  <Typography variant="caption" color="#6B7280" display="block" sx={{ mb: 1.5 }}>
                    Customize instructions for the AI model to extract fields, validate authenticity, and cross-match candidate data.
                  </Typography>
                  <TextField
                    multiline
                    rows={3}
                    fullWidth
                    size="small"
                    value={aiPromptInstructions}
                    onChange={(e) => setAiPromptInstructions(e.target.value)}
                    placeholder="Instructions for the AI verification model..."
                    sx={{
                      bgcolor: "#FFFFFF",
                      borderRadius: 1.5,
                      "& .MuiOutlinedInput-root": {
                        fontSize: "0.875rem",
                        "& fieldset": { borderColor: "#D8B4FE" },
                        "&:hover fieldset": { borderColor: "#9333EA" },
                        "&.Mui-focused fieldset": { borderColor: "#7E22CE" },
                      },
                    }}
                  />
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 1.5 }}>
                    <CheckCircleOutlineIcon sx={{ color: "#16A34A", fontSize: 16 }} />
                    <Typography variant="caption" color="#374151">
                      Upon saving, the backend AI extraction prompt will be auto-generated and synchronized with your configured fields.
                    </Typography>
                  </Box>
                </Box>
              )}
            </Box>
          </Box>
        )}

        {/* STEP 3: Review & Save (Mockup 5) */}
        {activeStep === 2 && (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
            <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2, bgcolor: "#F9FAFB" }}>
              <Typography variant="subtitle1" fontWeight={700} color="#111827" gutterBottom>
                Configuration Summary
              </Typography>
              <Stack direction={{ xs: "column", sm: "row" }} spacing={4} sx={{ mt: 1 }}>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Hiring Type
                  </Typography>
                  <Typography variant="body1" fontWeight={600}>
                    {hiringType === "BLUE_COLLAR" ? "Blue Collar" : "White Collar"}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Document Type
                  </Typography>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.2 }}>
                    <Typography variant="body1" fontWeight={600}>
                      {docMode === "CUSTOM" ? customDisplayName : (selectedDocObj?.display_name || "Document")}
                    </Typography>
                    {docMode === "CUSTOM" && (
                      <Chip label="Custom" size="small" color="primary" variant="outlined" sx={{ height: 20, fontSize: "0.65rem" }} />
                    )}
                  </Box>
                </Box>

              </Stack>
            </Paper>

            <Box>
              <Typography variant="subtitle1" fontWeight={700} color="#111827" gutterBottom>
                Fields to Extract ({fields.length})
              </Typography>
              <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
                <Table size="small">
                  <TableHead sx={{ bgcolor: "#F9FAFB" }}>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600 }}>Field Key</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Field Name</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Data Type</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Required</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {fields.map((f, idx) => (
                      <TableRow key={idx}>
                        <TableCell>
                          <code>{f.field_key}</code>
                        </TableCell>
                        <TableCell>{f.display_name}</TableCell>
                        <TableCell>
                          {DATA_TYPES.find((d) => d.value === f.data_type)?.label || f.data_type}
                        </TableCell>
                        <TableCell>{f.is_required ? "Yes" : "No"}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>

            {verificationType === "AI" ? (
              <Box
                sx={{
                  p: 2,
                  bgcolor: "#FAF5FF",
                  borderRadius: 2,
                  border: "1px solid #E9D5FF",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
                  <AutoAwesomeIcon sx={{ color: "#7E22CE", fontSize: 18 }} />
                  <Typography variant="subtitle2" fontWeight={700} color="#581C87">
                    AI Extraction Rules Configured
                  </Typography>
                </Box>
                <Typography variant="body2" color="#4B5563" sx={{ fontSize: "0.85rem", mb: 1.5 }}>
                  {aiPromptInstructions}
                </Typography>
                <Alert severity="success" sx={{ py: 0.5, borderRadius: 1.5, "& .MuiAlert-message": { fontSize: "0.8rem" } }}>
                  AI automated verification will run via LLM as soon as candidate documents are uploaded.
                </Alert>
              </Box>
            ) : (
              <Alert severity="info" sx={{ borderRadius: 2, fontSize: "0.85rem" }}>
                Uploaded documents will be queued for review based on configured extraction fields.
              </Alert>
            )}
          </Box>
        )}
      </DialogContent>

      {/* Footer Actions */}
      <DialogActions sx={{ px: 3, py: 2, borderTop: "1px solid #E5E7EB", justifyContent: "space-between" }}>
        {activeStep === 0 ? (
          <Button onClick={handleClose} disabled={saving} sx={{ textTransform: "none", color: "#6B7280" }}>
            Cancel
          </Button>
        ) : (
          <Button onClick={handleBack} disabled={saving} sx={{ textTransform: "none", color: "#374151" }}>
            Back
          </Button>
        )}

        {activeStep < 2 ? (
          <Button
            variant="contained"
            onClick={handleNext}
            disabled={saving}
            sx={{
              textTransform: "none",
              borderRadius: 2,
              px: 3,
              bgcolor: "#2563EB",
              "&:hover": { bgcolor: "#1D4ED8" },
            }}
          >
            Next
          </Button>
        ) : (
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={saving}
            startIcon={saving ? <CircularProgress size={18} color="inherit" /> : <CheckCircleOutlineIcon />}
            sx={{
              textTransform: "none",
              borderRadius: 2,
              px: 3,
              bgcolor: "#2563EB",
              "&:hover": { bgcolor: "#1D4ED8" },
            }}
          >
            {saving ? "Saving Configuration..." : "Save Configuration"}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default AddDocumentModal;
